import { Building2 } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL } from '@/lib/back-nav';

import { PageChrome } from '@/components/layout/page-chrome';
import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { StatePanel } from '@/components/ui/StatePanel';
import { usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';

import { Inbox } from './components/Inbox';
import { NotificationSettings } from './components/NotificationSettings';
import { InboxSkeleton } from './components/NotificationsSkeleton';
import { NotificationsTabs } from './components/NotificationsTabs';
import { panelId, tabId } from './tabs';
import { useNotificationsUrlState } from './use-url-state';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Configuración' },
  { label: 'Notificaciones', strong: true },
];

export const CONFIGURE_PERMISSION = 'system:notifications:configure';

function Frame({ children }: { children: ReactNode }): ReactElement {
  return (
    <>
      <PageChrome breadcrumb={BREADCRUMB} navContext="Notificaciones y escalamiento" />
      <BackLink target={TO_PANEL} />
      {children}
    </>
  );
}

/** Pantalla Notificaciones (so=370): bandeja de avisos de todo usuario y, con el permiso
 *  de configuración, reglas, escalamiento y plazos por etapa de la empresa activa. */
export function Notifications(): ReactElement {
  const perm = usePermissionState(CONFIGURE_PERMISSION);
  // Con /me en error no se sabe si hay permiso: null (no se toca la URL) y panel de error.
  const configAllowed = perm.state === 'granted' ? true : perm.state === 'denied' ? false : null;
  const { tab, filter, setTab, setFilter } = useNotificationsUrlState(configAllowed);
  const { activeCompanyId, isLoading: companyLoading } = useActiveCompany();

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (!companyLoading && !activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  const panel =
    activeCompanyId === null ? (
      <div className="card">
        <InboxSkeleton />
      </div>
    ) : tab === 'configuracion' ? (
      <NotificationSettings companyId={activeCompanyId} />
    ) : (
      <Inbox companyId={activeCompanyId} filter={filter} onFilter={setFilter} />
    );

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Notificaciones</h1>
          <p style={{ maxWidth: '70ch' }}>
            Avisos de las actividades a tu cargo y, para Calidad, las reglas de aviso y escalamiento
            por vencimiento.
          </p>
        </div>
        {configAllowed === true && <NotificationsTabs tab={tab} onChange={setTab} />}
      </div>
      <div
        className="tabpanel"
        role={configAllowed === true ? 'tabpanel' : undefined}
        id={configAllowed === true ? panelId(tab) : undefined}
        aria-labelledby={configAllowed === true ? tabId(tab) : undefined}
      >
        {panel}
      </div>
    </Frame>
  );
}
